<template>
  <!-- Публичный профиль автора (/user/<имя>) - панель поверх карты, как
       карточка маршрута: на десктопе в левой панели App.vue, на телефоне
       внутри MobileProfileSheet.vue (compact: шапка - зона перетаскивания). -->
  <div class="flex flex-col h-full overflow-hidden bg-[hsl(var(--card))]" data-profile-panel>
    <div
      ref="headerRef"
      class="shrink-0 px-4 pt-2 pb-3 bg-sheet border-b-[1.5px] border-border"
      :style="compact ? 'touch-action: none' : undefined"
      v-bind="compact ? { 'data-sheet-drag-handle': true } : {}"
    >
      <div class="flex items-start gap-2">
        <!-- «Назад» - на предыдущую запись истории (карточку маршрута), без неё - в покой. -->
        <button
          type="button"
          data-card-back
          @click="$emit('back')"
          class="-ml-2 shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-ink2 hover:bg-accent hover:text-foreground active:bg-accent"
          :aria-label="$t('routeDetails.back')"
        >
          <ChevronLeft class="w-6 h-6" :stroke-width="2" aria-hidden="true" />
        </button>
        <div
          v-if="profile"
          class="shrink-0 mt-0.5 w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary)/0.3)] to-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.2)] overflow-hidden"
        >
          <img v-if="profile.avatar_url" :src="resolveMediaUrl(profile.avatar_url) ?? undefined" alt="" class="w-full h-full object-cover" />
          <span v-else class="text-[16px] font-semibold text-[hsl(var(--primary))]">{{ profile.username[0]?.toUpperCase() }}</span>
        </div>
        <div class="flex-1 min-w-0 pt-0.5">
          <template v-if="profile">
            <h2 data-profile-name class="text-[17px] leading-tight text-foreground line-clamp-2">{{ fullName || '@' + profile.username }}</h2>
            <p v-if="fullName" data-profile-username class="mt-0.5 text-[12px] leading-tight text-ink2">@{{ profile.username }}</p>
          </template>
          <h2 v-else-if="!loading" class="text-[17px] leading-tight text-foreground">{{ $t('publicProfile.notFoundTitle') }}</h2>
        </div>
        <button
          type="button"
          data-card-close
          class="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-muted text-ink2 hover:bg-accent hover:text-foreground"
          :aria-label="$t('routeDetails.close')"
          @click="$emit('close')"
        >
          <X class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
      <div v-if="loading" class="py-12 text-center">
        <Loader2 class="w-6 h-6 animate-spin inline text-ink2" />
      </div>

      <div v-else-if="!profile" data-profile-not-found class="py-10 text-center">
        <div class="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center bg-muted text-ink2">
          <UserX class="w-6 h-6" />
        </div>
        <p class="text-[15px] font-semibold text-foreground">{{ $t('publicProfile.notFoundTitle') }}</p>
        <p class="text-[13px] text-ink2 mt-1">{{ $t('publicProfile.notFoundSubtitle') }}</p>
      </div>

      <div v-else-if="isPrivate" data-profile-private class="py-10 text-center">
        <div class="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center bg-muted text-ink2">
          <Lock class="w-6 h-6" />
        </div>
        <p class="text-[15px] font-semibold text-foreground">{{ $t('publicProfile.private') }}</p>
      </div>

      <section v-else data-profile-routes>
        <h3 class="mb-3 text-[15px] font-semibold text-foreground">{{ $t('publicProfile.routesTitle') }}<span v-if="profile.routes.length" class="num ml-2 text-[13px] font-medium text-ink2">{{ profile.routes.length }}</span></h3>
        <div v-if="profile.routes.length" class="flex flex-col gap-3">
          <RouteCardLarge
            v-for="r in profile.routes"
            :key="r.id"
            :route="r"
            :saved="false"
            :authed="false"
            hide-save
            @open="$emit('open-route', r.id)"
          />
        </div>
        <p v-else data-routes-empty class="rounded-xl border border-border bg-sheet px-4 py-6 text-center text-[14px] text-ink2">{{ $t('publicProfile.routesEmpty') }}</p>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ChevronLeft, Loader2, Lock, UserX, X } from "lucide-vue-next";
import RouteCardLarge from "./route/RouteCardLarge.vue";
import type { RouteMapItem } from "../types/routes";
import api, { resolveMediaUrl } from "../api";

const props = defineProps<{ username: string; compact?: boolean }>();
defineEmits<{
  (e: "back"): void;
  (e: "close"): void;
  (e: "open-route", id: number): void;
}>();

interface PublicProfile {
  username: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  profile_visibility: string;
  routes: RouteMapItem[];
}

const profile = ref<PublicProfile | null>(null);
const loading = ref(true);

const isPrivate = computed(() => profile.value?.profile_visibility === "private");
// Закрытый профиль - только аватар и @имя, без ФИО.
const fullName = computed(() => {
  if (!profile.value || isPrivate.value) return "";
  return [profile.value.first_name, profile.value.last_name].filter(Boolean).join(" ");
});

// Ответ на устаревший запрос (имя успело смениться) не перетирает текущий.
let requestSeq = 0;
const load = async () => {
  const seq = ++requestSeq;
  loading.value = true;
  profile.value = null;
  try {
    const res = await api.get<PublicProfile>(`/users/${encodeURIComponent(props.username)}`);
    if (seq === requestSeq) profile.value = res.data;
  } catch {
    if (seq === requestSeq) profile.value = null;
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
};

onMounted(load);
watch(() => props.username, load);

// Высота шапки - для шторки на телефоне (нижняя граница перетаскивания).
const headerRef = ref<HTMLElement | null>(null);
const headerHeight = ref(0);
let headerObserver: ResizeObserver | null = null;
watch(headerRef, (el) => {
  headerObserver?.disconnect();
  if (!el || typeof ResizeObserver === "undefined") return;
  headerObserver = new ResizeObserver(() => {
    headerHeight.value = el.getBoundingClientRect().height;
  });
  headerObserver.observe(el);
});
onBeforeUnmount(() => headerObserver?.disconnect());

defineExpose({ headerHeight });
</script>
